import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { ContractMetricListResponse } from '../IMessageBubbleInterface';
import { findValueByAliases, formatCurrency } from '../MessageBubbleHelpers';

import {
    AssetAvatarIcon,
    AssetCardShell,
    AssetFieldGrid,
    AssetLocationTile,
    CardIntro,
    ContractSerialOnlyAssetCard,
} from './sharedCardHelpers';

export const ContractMetricListCard: React.FC<{ data: ContractMetricListResponse | null }> = ({ data }) => {
    const contractMetricList = data;
    if (!contractMetricList) return null;

    const isCurrencyLike = (value: string) => /^\$?\s*[-]?[\d,.]+$/.test((value || '').trim());
    const shouldFormatMetricAsCurrency = (label: string, value: string) => {
        const normalizedValue = (value || '').trim();
        if (!normalizedValue || normalizedValue.includes('%') || !isCurrencyLike(normalizedValue)) {
            return false;
        }

        if (normalizedValue.includes('$')) {
            return true;
        }

        const normalizedLabel = (label || '').toLowerCase();
        if (
            /(payments?\s*done|number\s*of\s*payments?|remaining\s*payments?|payments?\s*completed|installments?\s*done|\bterm\b|\bcount\b)/.test(
                normalizedLabel,
            )
        ) {
            return false;
        }
        return /(amount|balance|payment|due|principal|interest|fee|financed|paid|outstanding|total|past due)/.test(
            normalizedLabel,
        );
    };

    return (
        <Box>
            <CardIntro text={contractMetricList.intro} />

            <Stack gap={0.9}>
                {contractMetricList.rows.map((row, rowIdx) => {
                    const getMetricValue = (...labels: string[]) => findValueByAliases(row.metrics, labels);

                    const assetName = getMetricValue('Asset Name');
                    const assetDescription = getMetricValue('Asset Description', 'Description');
                    const serialNumber = getMetricValue(
                        'Serial Number',
                        'Serial Number (VIN)',
                        'VIN',
                        'Vehicle Identification Number',
                    );
                    const make = getMetricValue('Make');
                    const model = getMetricValue('Model');
                    const year = getMetricValue('Year');
                    const address = getMetricValue('Address');
                    const city = getMetricValue('City');
                    const state = getMetricValue('State');
                    const postalCode = getMetricValue('Postal Code');
                    const country = getMetricValue('Country');
                    const locationFromMetric = getMetricValue('Location', 'Asset Location');

                    const looksLikeAssetMetricRow = Boolean(
                        assetName ||
                            assetDescription ||
                            serialNumber ||
                            make ||
                            model ||
                            year ||
                            locationFromMetric ||
                            address ||
                            city ||
                            state ||
                            postalCode ||
                            country,
                    );

                    if (looksLikeAssetMetricRow) {
                        const isContractSerialOnlyRow = Boolean(
                            serialNumber &&
                                row.contractId &&
                                !assetName &&
                                !assetDescription &&
                                !make &&
                                !model &&
                                !year &&
                                !locationFromMetric &&
                                !address &&
                                !city &&
                                !state &&
                                !postalCode &&
                                !country,
                        );

                        if (isContractSerialOnlyRow) {
                            return (
                                <ContractSerialOnlyAssetCard
                                    key={`${row.contractId}-${rowIdx}`}
                                    contractId={row.contractId}
                                    serialNumber={serialNumber}
                                />
                            );
                        }

                        const location =
                            locationFromMetric ||
                            [address, city, state, postalCode, country].filter(Boolean).join(', ') ||
                            'N/A';
                        const assetTitle = assetDescription || assetName || `Contract ${row.contractId}`;
                        const assetSubtitle = assetName || `Contract ${row.contractId}`;

                        return (
                            <AssetCardShell key={`${row.contractId}-${rowIdx}`}>
                                <Stack direction="row" gap={0.85} alignItems="center" sx={{ mb: 0.95 }}>
                                    <AssetAvatarIcon />

                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.fullBlack,
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {assetTitle}
                                        </Typography>
                                        {row.contractId && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: colors.fullBlack,
                                                    fontWeight: 700,
                                                    mt: 0.05,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {`Contract ID: ${row.contractId}`}
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{ color: colors.greyPreset[60], fontWeight: 500, mt: 0.05 }}
                                        >
                                            {assetSubtitle}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack gap={0.8}>
                                    <AssetFieldGrid
                                        keyPrefix={`${row.contractId}-${rowIdx}`}
                                        fields={[
                                            ['Serial Number', serialNumber || 'N/A'],
                                            ['Make', make || 'N/A'],
                                        ]}
                                    />

                                    <AssetFieldGrid
                                        keyPrefix={`${row.contractId}-${rowIdx}`}
                                        fields={[
                                            ['Model', model || 'N/A'],
                                            ['Year', year || 'N/A'],
                                        ]}
                                    />

                                    <AssetLocationTile location={location} />
                                </Stack>
                            </AssetCardShell>
                        );
                    }

                    const isSingleMetric = row.metrics.length === 1;

                    return (
                        <Box
                            key={`${row.contractId}-${rowIdx}`}
                            sx={{
                                border: `1px solid ${colors.greyPreset[20]}`,
                                borderRadius: '12px',
                                backgroundColor: colors.fullWhite,
                                boxShadow: '0 1px 8px rgba(16, 24, 40, 0.05)',
                                p: 1,
                            }}
                        >
                            {isSingleMetric ? (
                                (() => {
                                    const metric = row.metrics[0];
                                    const normalizedValue = (metric.value || '').trim();
                                    const formattedValue = shouldFormatMetricAsCurrency(metric.label, normalizedValue)
                                        ? formatCurrency(normalizedValue)
                                        : normalizedValue || '-';

                                    return (
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '25% 75%' },
                                                gap: 0.9,
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0, pr: { xs: 0, sm: 0.2 } }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[50],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.4,
                                                        mb: 0.15,
                                                    }}
                                                >
                                                    Contract
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        lineHeight: 1.25,
                                                    }}
                                                >
                                                    #{row.contractId}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    minWidth: 0,
                                                    px: 1,
                                                    py: 0.8,
                                                    borderRadius: '10px',
                                                    border: `1px solid ${colors.greyPreset[30]}`,
                                                    background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[50],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.4,
                                                    }}
                                                >
                                                    {metric.label}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 800,
                                                        fontSize: '0.95rem',
                                                        lineHeight: 1.3,
                                                        mt: 0.2,
                                                    }}
                                                >
                                                    {formattedValue}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })()
                            ) : (
                                <Stack gap={0.7}>
                                    <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                        Contract {row.contractId}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 0.75,
                                        }}
                                    >
                                        {row.metrics.map((metric, metricIdx) => {
                                            const normalizedValue = (metric.value || '').trim();
                                            const formattedValue = shouldFormatMetricAsCurrency(
                                                metric.label,
                                                normalizedValue,
                                            )
                                                ? formatCurrency(normalizedValue)
                                                : normalizedValue || '-';

                                            return (
                                                <Box
                                                    key={`${row.contractId}-${metric.label}-${metricIdx}`}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        border: `1px solid ${colors.greyPreset[30]}`,
                                                        backgroundColor: colors.greyPreset[5],
                                                        px: 0.9,
                                                        py: 0.7,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: colors.greyPreset[50],
                                                            fontWeight: 700,
                                                            display: 'block',
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        {metric.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: colors.fullBlack,
                                                            fontWeight: 700,
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {formattedValue}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Stack>
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {contractMetricList.followUp && (
                <Typography variant="body2" sx={{ mt: 1.1, color: colors.greyPreset[60], lineHeight: 1.55 }}>
                    {contractMetricList.followUp}
                </Typography>
            )}
        </Box>
    );
};
